import React, { useState, useEffect, useMemo, useCallback } from "react";
import { TextField, MenuItem, IconButton, Stack, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import StandardTable from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";
import ConfirmDialog from "../components/common/ConfirmDialog";
import CreateUserModal from "../components/users/CreateUserModal";
import UpdateUserModal from "../components/users/UpdateUserModal";
import ViewUserModal from "../components/users/ViewUserModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import {
  getUsers,
  getUserById,
  deleteUser,
  resetUserServerStatus,
} from "../redux/actions/userActions";
import { getRoles } from "../redux/actions/roleActions";

import { castEnumForDisplay } from "../helpers/enumHelpers";

const UsersPage = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  // --- Redux State ---
  const {
    users,
    loadingUsers,
    errorLoadingUsers,
    pagination,
    serverStatus,
    loadingUserCreate,
    errorCreatingUser,
    loadingUserUpdate,
    errorUpdatingUser,
    loadingUserDelete,
    userInfo,
    user,
    loadingUser,
    errorLoadingUser,
  } = useSelector((state) => state.user);
  const { roles } = useSelector((state) => state.role);

  // --- Local State ---
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);

  const [filters, setFilters] = useState({ role: "", status: "" });
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [userForOperation, setUserForOperation] = useState(null);

  // --- Permissions ---
  const canCreateUser = hasPermission("user:create");
  const canEditUser = hasPermission("user:update");
  const canDeleteUser = hasPermission("user:delete");
  const canViewUser = hasPermission("user:read");

  // --- Data Fetching ---
  const fetchUsers = useCallback(() => {
    if (!roles || roles.length === 0) dispatch(getRoles());

    dispatch(
      getUsers({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        role: filters.role,
        status: filters.status,
      }),
    );
  }, [
    roles,
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    filters.role,
    filters.status,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const roleOptions = useMemo(
    () =>
      roles?.map((r) => ({
        value: r.name,
        label: castEnumForDisplay(r.name),
      })) || [],
    [roles],
  );

  // --- Modal Handlers ---
  const handleOpenCreate = () => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(() => {
    if (!loadingUserCreate) setIsCreateModalOpen(false);
  }, [loadingUserCreate]);

  const handleOpenUpdate = (user) => {
    setUserForOperation(user);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingUserUpdate) {
      setIsUpdateModalOpen(false);
      setUserForOperation(null);
    }
  }, [loadingUserUpdate]);

  const handleOpenDelete = (user) => {
    setUserForOperation(user);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingUserDelete) {
      setIsDeleteModalOpen(false);
      setUserForOperation(null);
    }
  }, [loadingUserDelete]);

  const handleOpenView = (user) => {
    dispatch(getUserById(user.id));
    setIsViewModalOpen(true);
  };
  const handleCloseView = () => setIsViewModalOpen(false);

  const handleDeleteConfirm = () => {
    if (userForOperation) {
      dispatch(deleteUser(userForOperation.id));
    }
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetUserServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  // --- Table Configuration ---
  const columns = useMemo(
    () => [
      {
        id: "name",
        label: "Full Name",
        minWidth: 200,
        format: (_, row) => (
          <span style={{ fontWeight: 600 }}>
            {row.firstName} {row.lastName}
          </span>
        ),
      },
      { id: "email", label: "Email Address", minWidth: 250 },
      {
        id: "userRole", // Matches UserResponseDto
        label: "Role",
        minWidth: 150,
        format: (value) => castEnumForDisplay(value),
      },
      {
        id: "userStatus", // Matches UserResponseDto
        label: "Status",
        minWidth: 120,
        format: (value) => <StatusChip status={value} />,
      },
      {
        id: "createdAt",
        label: "Joined",
        minWidth: 120,
        format: (value) =>
          value ? new Date(value).toLocaleDateString() : "N/A",
      },
    ],
    [],
  );

  const renderRowActions = (row) => (
    <Stack direction="row" spacing={1} justifyContent="center">
      {canViewUser && (
        <Tooltip title="View Details">
          <IconButton
            size="small"
            color="info"
            onClick={() => handleOpenView(row)}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {canEditUser && (
        <Tooltip title="Edit User">
          <IconButton
            size="small"
            color="secondary"
            onClick={() => handleOpenUpdate(row)}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {canDeleteUser && (
        <Tooltip title="Delete User">
          <IconButton
            size="small"
            color="error"
            onClick={() => handleOpenDelete(row)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  return (
    <PageContainer
      title="User Management"
      subtitle="Manage system access, roles, and administrative accounts."
      actions={
        canCreateUser && (
          <AppButton
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleOpenCreate}
          >
            Create New User
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by name or email..."
      >
        <TextField
          select
          label="Role"
          size="small"
          value={filters.role}
          onChange={(e) => setFilters({ ...filters, role: e.target.value })}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Roles</MenuItem>
          {roleOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          size="small"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Status</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="INACTIVE">Inactive</MenuItem>
        </TextField>
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={users}
        loading={loadingUsers}
        error={errorLoadingUsers}
        onRetry={fetchUsers}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(newPage) =>
          setPageConfig({ ...pageConfig, page: newPage })
        }
        onRowsPerPageChange={(newLimit) =>
          setPageConfig({ ...pageConfig, limit: newLimit, page: 1 })
        }
        rowAction={renderRowActions}
      />

      {/* --- MODALS --- */}
      {isCreateModalOpen && (
        <CreateUserModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          userInfo={userInfo}
          loading={loadingUserCreate}
          error={errorCreatingUser}
        />
      )}
      {isUpdateModalOpen && userForOperation && (
        <UpdateUserModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          user={userForOperation}
          userInfo={userInfo}
          loading={loadingUserUpdate}
          error={errorUpdatingUser}
        />
      )}
      {isViewModalOpen && (
        <ViewUserModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          user={user}
          loading={loadingUser}
          error={errorLoadingUser}
        />
      )}
      {isDeleteModalOpen && userForOperation && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete User"
          message={`Are you sure you want to delete "${userForOperation.firstName} ${userForOperation.lastName}"?`}
          loading={loadingUserDelete}
        />
      )}
    </PageContainer>
  );
};

export default UsersPage;
